import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TextArea, MultiSelect, Button, type MultiSelectOption } from '@/shared/ui'
import { ApiError } from '@/shared/api'
import { useBotStore } from './botStore'
import { useBotModels, useBotSettings, useAskTurn } from './hooks/useBot'
import { getModelTagline, estimateAnswerEur, formatEstimate } from './botModelMeta'

interface BotComposerProps {
  /** Active conversation, or null to let the ask create a new one. */
  conversationId: number | null
  value: string
  onValueChange: (value: string) => void
}

/**
 * Footer input row. Lives directly on the panel's Liquid Glass chrome (not
 * the flat thread above it), so its fields use variant="glass".
 */
export function BotComposer({ conversationId, value, onValueChange }: BotComposerProps) {
  const { t, i18n } = useTranslation('bot')
  const { data: models } = useBotModels()
  const settingsQuery = useBotSettings()
  const settings = settingsQuery.data
  const selectedModels = useBotStore((s) => s.selectedModels)
  const setSelectedModels = useBotStore((s) => s.setSelectedModels)
  const askTurn = useAskTurn()

  const [error, setError] = useState<string | null>(null)
  const [budgetExceeded, setBudgetExceeded] = useState(false)

  // Default selection: settings.defaultModels ∩ (enabled ∩ configured), else
  // the first configured model. Seeds EXACTLY ONCE per mount, after models
  // are loaded and the settings query has settled (success or error) — and
  // only when the persisted selection is empty at that moment. Owner-reported
  // bug: keying this off selectedModels.length re-fired the effect whenever
  // the user cleared the last chip and force-re-selected the defaults, making
  // "no model selected" impossible. A deliberate clear must stick.
  const seededRef = useRef(false)
  const settingsSettled = !settingsQuery.isPending
  useEffect(() => {
    if (seededRef.current || !models || !settingsSettled) return
    seededRef.current = true
    if (selectedModels.length > 0) return
    const configuredIds = models.filter((m) => m.configured).map((m) => m.modelId)
    const fromSettings = (settings?.defaultModels ?? []).filter((id) => configuredIds.includes(id))
    if (fromSettings.length > 0) setSelectedModels(fromSettings.slice(0, 3))
    else if (configuredIds.length > 0) setSelectedModels([configuredIds[0]])
    // (fromSettings IS the chat list when non-empty — see chatModels below.)
  }, [models, settings, settingsSettled, selectedModels.length, setSelectedModels])

  // Owner directive (2026-08-31): the CHAT picker shows only models the user
  // can actually use — the Standardmodelle chosen in Settings, filtered to
  // configured ones. Unconfigured models never appear here (Settings remains
  // the curation surface that lists everything). No Settings selection (or
  // none of it configured) -> fall back to ALL configured models, so chat is
  // never dead-ended by an empty Settings choice.
  const configuredModels = (models ?? []).filter((m) => m.configured)
  const chosenIds = (settings?.defaultModels ?? []).filter((id) => configuredModels.some((m) => m.modelId === id))
  const chatModels = chosenIds.length > 0 ? configuredModels.filter((m) => chosenIds.includes(m.modelId)) : configuredModels
  // note = tagline + estimated cost per answer (Design Spec round 2 §4) —
  // chatModels is already filtered to configured models only (see above), so
  // no configured-gate is needed here (contrast SettingsTab.tsx, whose list
  // also carries unconfigured rows).
  const options: MultiSelectOption[] = chatModels.map((m) => {
    const tagline = getModelTagline(t, m.modelId)
    const estimate = estimateAnswerEur(m.price)
    const note = estimate != null ? `${tagline} · ${formatEstimate(estimate, t, i18n.language)}` : tagline
    return { value: m.modelId, label: `${m.label}`, note }
  })
  const noneConfigured = !!models && !models.some((m) => m.configured)

  // A persisted selection can reference models that just left the chat list
  // (Settings changed on another device, a key was removed). Prune it so no
  // invisible chip keeps counting toward the 3-model cap. Guarded to run
  // only when something actually changes — no set-state loops.
  useEffect(() => {
    if (!models || !settingsSettled) return
    const shown = new Set(chatModels.map((m) => m.modelId))
    const pruned = selectedModels.filter((id) => shown.has(id))
    if (pruned.length !== selectedModels.length) setSelectedModels(pruned)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chatModels derives from models+settings, both in deps
  }, [models, settings, settingsSettled, selectedModels, setSelectedModels])

  function handleModelsChange(values: string[]) {
    const shown = new Set(chatModels.map((m) => m.modelId))
    setSelectedModels(values.filter((v) => shown.has(v)).slice(0, 3))
  }

  function handleChange(next: string) {
    onValueChange(next)
    if (error) setError(null)
    // Editing the draft is the affordance to retry after a budget stop —
    // mirrors the existing `error` reset above rather than leaving Send
    // silently disabled for the rest of the session with no way out.
    if (budgetExceeded) setBudgetExceeded(false)
  }

  function handleSend() {
    const question = value.trim()
    // budgetExceeded is included here (not just in `disabled` below) so
    // pressing Enter in the TextArea can't bypass the same gate the Send
    // button enforces.
    if (!question || selectedModels.length === 0 || askTurn.isPending || budgetExceeded) return
    setError(null)
    askTurn.mutate(
      { question, models: selectedModels, conversationId: conversationId ?? undefined },
      {
        onSuccess: () => onValueChange(''),
        onError: (err: unknown) => {
          if (err instanceof ApiError && err.status === 429) {
            if (err.code === 'BOT_BUDGET_EXCEEDED') {
              setError(t('errors.budget'))
              setBudgetExceeded(true)
            } else {
              // LOCK_TIMEOUT / HOURLY_LIMIT — transient rate limiting, not a
              // hard budget stop, so Send stays usable for the next try.
              setError(t('errors.ratelimit'))
            }
          } else if (err instanceof ApiError) {
            setError(err.message)
          } else {
            setError(t('errors.generic'))
          }
        },
      },
    )
  }

  const rows = Math.min(5, Math.max(2, value.split('\n').length))
  const disabled = !value.trim() || selectedModels.length === 0 || budgetExceeded || askTurn.isPending

  return (
    <div className="border-t border-white/40 dark:border-white/10 p-3 space-y-2 bg-surface-container-lowest">
      {error && (
        <div
          role="alert"
          className="text-xs text-on-error-container bg-error-container rounded-lg px-3 py-2"
        >
          {error}
        </div>
      )}
      <span className="block text-[10px] font-medium uppercase tracking-wider text-on-surface-variant">{t('composer.models')}</span>
      <MultiSelect
        ariaLabel={t('composer.models')}
        options={options}
        selected={selectedModels}
        onChange={handleModelsChange}
        max={3}
        placeholder={t('composer.maxModels', { max: 3 })}
        searchPlaceholder={t('composer.search')}
        noResultsLabel={t('composer.noResults')}
        removeLabel={(optLabel) => t('composer.removeModel', { label: optLabel })}
        size="compact"
        variant="glass"
        menu="float"
        placement="up"
      />
      {noneConfigured && (
        <p className="text-xs text-on-surface-variant">{t('composer.noneConfigured')}</p>
      )}
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <TextArea
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={(e) => {
              // isComposing guards IME confirmation (e.g. JP/CN/KR input
              // methods use Enter to confirm a candidate, not to submit).
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault()
                handleSend()
              }
            }}
            rows={rows}
            maxLength={2000}
            placeholder={t('composer.placeholder')}
            aria-label={t('composer.placeholder')}
            variant="glass"
          />
          {value.length > 1800 && (
            <div className="text-right text-[10px] text-on-surface-variant mt-0.5 tabular-nums">
              {t('composer.counter', { count: value.length, max: 2000 })}
            </div>
          )}
        </div>
        <Button
          variant="primary"
          icon="send"
          loading={askTurn.isPending}
          disabled={disabled}
          onClick={handleSend}
          aria-label={t('composer.send')}
        />
      </div>
    </div>
  )
}
